import type { ReactNode } from "react";

const B = ({ children }: { children: ReactNode }) => (
  <strong className="font-medium text-[var(--color-fg)]">{children}</strong>
);

const moats: { title: string; description: ReactNode; icon: ReactNode; primary: boolean }[] = [
  {
    title: "Ownership",
    description: <>SaaS vendors can be switched out. When you own the firm and the platform, <B>competitors can&apos;t displace you</B>. Each acquisition locks in revenue and data permanently.</>,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /><circle cx="12" cy="16" r="1" /></svg>
    ),
    primary: true,
  },
  {
    title: "Data Flywheel",
    description: <>Every case processed generates training data. Every partner adds new benchmarks. Harness engineering turns this into autonomous agent improvement. <B>Competitors without real industry data cannot replicate this</B>.</>,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /><polyline points="21 3 21 12 12 12" /></svg>
    ),
    primary: true,
  },
  {
    title: "Compliance DNA",
    description: <>Team built and operated under FCA-regulated fintech. Compliance, audit, and governance are <B>foundational, not bolted on</B>. Domain expertise across immigration, finance, and education.</>,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg>
    ),
    primary: false,
  },
  {
    title: "Global Replicability",
    description: <>Built for UK, already expanding. ProActive Legal (AU/NZ) deploying Xeni. Singapore partner in pipeline. The playbook crosses borders and the <B>data compounds across jurisdictions</B>.</>,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
    ),
    primary: false,
  },
];

export function V2WhyUsSlide({ isActive: _ }: { isActive: boolean }) {
  const primaryMoats = moats.filter((m) => m.primary);
  const secondaryMoats = moats.filter((m) => !m.primary);

  return (
    <div className="flex flex-col h-full">
      <h2 className="text-[32px] font-light tracking-tight mb-5 font-[family-name:var(--font-heading)]">
        Four moats. One compounding advantage.
      </h2>

      <div className="grid grid-cols-2 gap-3 mb-3">
        {primaryMoats.map((m) => (
          <div key={m.title} className="border border-[var(--color-accent)]/30 rounded-sm p-5">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-[var(--color-accent)] shrink-0">{m.icon}</span>
              <h3 className="text-[15px] font-medium font-[family-name:var(--font-heading)]">{m.title}</h3>
            </div>
            <p className="text-[12px] leading-relaxed text-[var(--color-fg-secondary)] font-light">{m.description}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {secondaryMoats.map((m) => (
          <div key={m.title} className="border border-[var(--color-border)] rounded-sm p-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-[var(--color-fg)] shrink-0">{m.icon}</span>
              <h3 className="text-[15px] font-medium font-[family-name:var(--font-heading)]">{m.title}</h3>
            </div>
            <p className="text-[12px] leading-relaxed text-[var(--color-fg-secondary)] font-light">{m.description}</p>
          </div>
        ))}
      </div>

      <div className="text-left mt-auto mb-8 pb-4 border-b border-[var(--color-border)]">
        <p className="text-[12px] text-[var(--color-fg-secondary)] font-light">
          Own the firm. Own the data. <strong className="font-medium text-[var(--color-fg)]">The moat compounds with every acquisition.</strong>
        </p>
      </div>
    </div>
  );
}
